import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';

/* ------------------------------------------------------------------ */
/*  Scroll progress — a hairline across the very top of the page       */
/* ------------------------------------------------------------------ */
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.4 });
  return (
    <motion.div
      aria-hidden="true"
      className="fixed inset-x-0 top-0 z-[80] h-px origin-left"
      style={{ scaleX, background: 'linear-gradient(to right, var(--fg-4), var(--fg))' }}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Atmosphere — grain, aurora glow, vignette. Fixed, non-interactive  */
/* ------------------------------------------------------------------ */
export function Atmosphere() {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  // Glows sink and dim slightly as the page scrolls so content stays king.
  const glowY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.35], [1, 0.45]);

  return (
    <>
      {/* Aurora — two warm-white pools drifting behind everything. */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <motion.div style={{ y: reduce ? 0 : glowY, opacity: reduce ? 0.5 : glowOpacity }} className="absolute inset-0">
          <div
            className="drift-slow absolute -top-[32rem] left-1/2 h-[46rem] w-[72rem] -translate-x-1/2 rounded-full blur-3xl"
            style={{ background: 'radial-gradient(closest-side, rgba(242,240,235,0.075), transparent)' }}
          />
          <div
            className="drift-slower absolute -left-64 top-[38%] h-[34rem] w-[40rem] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(closest-side, rgba(242,240,235,0.045), transparent)' }}
          />
          <div
            className="drift-slow absolute -right-72 top-[62%] h-[30rem] w-[36rem] rounded-full blur-3xl"
            style={{ background: 'radial-gradient(closest-side, rgba(242,240,235,0.035), transparent)', animationDelay: '-13s' }}
          />
        </motion.div>
        {/* Vignette to keep focus centered. */}
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 120% 90% at 50% 40%, transparent 55%, rgba(0,0,0,0.5) 100%)' }}
        />
      </div>
      {!reduce && <div aria-hidden="true" className="grain" />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Tilt — subtle pointer-driven parallax for a media card             */
/* ------------------------------------------------------------------ */
export function Tilt({ children, max = 4, className }: { children: ReactNode; max?: number; className?: string }) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 140, damping: 18 });
  const sry = useSpring(ry, { stiffness: 140, damping: 18 });

  if (reduce) return <div className={className}>{children}</div>;

  const onMove = (e: React.PointerEvent) => {
    const el = ref.current;
    if (!el || e.pointerType !== 'mouse') return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(px * max * 2);
    rx.set(-py * max * 2);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      style={{ rotateX: srx, rotateY: sry, transformPerspective: 1100 }}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  PulseDot — live status indicator                                   */
/* ------------------------------------------------------------------ */
export function PulseDot({ tone = 'var(--ok)' }: { tone?: string }) {
  return (
    <span className="relative inline-flex h-1.5 w-1.5 flex-none">
      <span aria-hidden="true" className="pulse-dot absolute inset-0 rounded-full" style={{ background: tone }} />
      <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: tone }} />
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Marquee — infinite single-row ticker (duplicated track, a11y-safe) */
/* ------------------------------------------------------------------ */
export function Marquee({ items, label }: { items: string[]; label?: string }) {
  const row = [...items, ...items];
  return (
    <div className="marquee" role={label ? 'img' : undefined} aria-label={label}>
      <div className="marquee-track gap-14 pr-14">
        {row.map((item, i) => (
          <span key={`${item}-${i}`} aria-hidden={i >= items.length} className="mono whitespace-nowrap text-sm text-[color:var(--fg-3)]">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  CountUp — number that counts up when scrolled into view            */
/* ------------------------------------------------------------------ */
export function CountUp({ to, suffix = '', duration = 1.6 }: { to: number; suffix?: string; duration?: number }) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState(reduce ? to : 0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || reduce) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) return;
        started.current = true;
        const t0 = performance.now();
        const tick = (t: number) => {
          const p = Math.min((t - t0) / (duration * 1000), 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setValue(Math.round(eased * to));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration, reduce]);

  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  );
}
